import {
  Checkbox as MantineCheckbox,
  Chip as MantineChip,
  Radio as MantineRadio,
  SegmentedControl as MantineSegmentedControl,
  Switch as MantineSwitch,
} from "@mantine/core";
import * as React from "react";

import type { ElementProps, Size, SizeValue, StyleProps } from "../primitives/style-props";

import classes from "./toggles.module.css";

/**
 * Mantine 7 types `Radio.Group`'s and `Chip`'s `size` as the five theme keys alone, so the owned
 * size string reaches them only as one of those keys (SC-2703). Every call site passes a key; any
 * other string resolved to no control size in Mantine 6 as well.
 */
const toThemeSizeKey = (size: string | undefined): Exclude<Size, number> | undefined =>
  size === "xs" || size === "sm" || size === "md" || size === "lg" || size === "xl" ? size : undefined;

/**
 * The label/feedback surface shared by the inline toggle controls (Checkbox, Switch, Radio).
 * `form.getInputProps("...", { type: "checkbox" })` feeds `checked`/`onChange` through the element
 * props and `error` through this surface, so the @mantine/form carve-out (see README) holds with no
 * change to any form. Narrower than the text-input field surface: no `withAsterisk`, no
 * `descriptionProps` — no toggle call site uses either.
 */
interface ToggleLabelProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
}

/** The group-level label surface. Matches Mantine's InputWrapperBaseProps subset the groups use. */
interface GroupLabelProps {
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: React.ReactNode;
  withAsterisk?: boolean;
}

export interface CheckboxProps extends Omit<ElementProps<"input">, "size" | "type">, StyleProps, ToggleLabelProps {
  /** Overrides `checked` while set; the three call sites drive tri-state header checkboxes. */
  indeterminate?: boolean;
  /** Non-numeric: Mantine resolves control size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

/**
 * Mantine 7 fills an indeterminate box like a checked one; Mantine 6 kept an unchecked indeterminate
 * box white with a dash in the text color. The owned slot classes restore that look (SC-2703; see
 * `toggles.module.css`).
 */
const checkboxClassNames = { input: classes.checkboxInput, icon: classes.checkboxIcon };

const CheckboxBase = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(props, ref) {
  return <MantineCheckbox {...props} classNames={checkboxClassNames} ref={ref} />;
});

export interface CheckboxGroupProps extends Omit<ElementProps<"div">, "onChange" | "defaultValue">, StyleProps, GroupLabelProps {
  children: React.ReactNode;
  /** Values of the checked child checkboxes. */
  value?: string[];
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  size?: Exclude<SizeValue, number>;
}

const CheckboxGroup = React.forwardRef<HTMLDivElement, CheckboxGroupProps>(function CheckboxGroup({ children, ...props }, ref) {
  return (
    <MantineCheckbox.Group {...props} ref={ref}>
      {children}
    </MantineCheckbox.Group>
  );
});

export const Checkbox = Object.assign(CheckboxBase, { Group: CheckboxGroup });

export interface SwitchProps extends Omit<ElementProps<"input">, "size" | "type">, StyleProps, ToggleLabelProps {
  /** Side of the track the label renders on; "right" by default. */
  labelPosition?: "left" | "right";
  /** Non-numeric: Mantine resolves control size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(function Switch(props, ref) {
  return <MantineSwitch {...props} ref={ref} />;
});

export interface RadioProps extends Omit<ElementProps<"input">, "size">, StyleProps, ToggleLabelProps {
  /** Non-numeric: Mantine resolves control size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

const RadioBase = React.forwardRef<HTMLInputElement, RadioProps>(function Radio(props, ref) {
  return <MantineRadio {...props} ref={ref} />;
});

export interface RadioGroupProps extends Omit<ElementProps<"div">, "onChange" | "defaultValue">, StyleProps, GroupLabelProps {
  children: React.ReactNode;
  /** Value of the checked child radio. */
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /** Name attribute shared by the child radio inputs; a unique id by default. */
  name?: string;
  size?: Exclude<SizeValue, number>;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup({ children, size, ...props }, ref) {
  return (
    <MantineRadio.Group {...props} size={toThemeSizeKey(size)} ref={ref}>
      {children}
    </MantineRadio.Group>
  );
});

export const Radio = Object.assign(RadioBase, { Group: RadioGroup });

export interface SegmentedControlItem {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps extends Omit<ElementProps<"div">, "onChange" | "defaultValue">, StyleProps {
  /**
   * Object form only: every call site passes `{ label, value }` entries, so the bare-string arm of
   * Mantine's union is deliberately not restated.
   */
  data: SegmentedControlItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Non-numeric: Mantine resolves control size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

export const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(function SegmentedControl(props, ref) {
  return <MantineSegmentedControl {...props} ref={ref} />;
});

export interface ChipProps extends Omit<ElementProps<"input">, "size" | "onChange" | "type">, StyleProps {
  children: React.ReactNode;
  /** Checked state for a standalone chip; inside Chip.Group the group's `value` drives it. */
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  variant?: "outline" | "filled" | "light";
  /** Non-numeric: Mantine resolves control size by theme key only, so a number would silently produce no size. */
  size?: Exclude<SizeValue, number>;
}

/** Mantine 6 defaulted Chip to the `outline` variant; Mantine 7 defaults to `filled`, so the owned default stays `outline` (SC-2703). */
const ChipBase = React.forwardRef<HTMLInputElement, ChipProps>(function Chip({ children, variant = "outline", size, ...props }, ref) {
  return (
    <MantineChip {...props} variant={variant} size={toThemeSizeKey(size)} ref={ref}>
      {children}
    </MantineChip>
  );
});

/**
 * Generic over `multiple`, exactly as Mantine types it: `multiple` flips the `value` contract
 * between `string` and `string[]`. Restated rather than simplified because one call site
 * (EngineControlBar) uses the `multiple` arm and one (DateFilterWithPresets) uses the single arm.
 */
export interface ChipGroupProps<Multiple extends boolean = false> {
  multiple?: Multiple;
  value?: Multiple extends true ? string[] : string;
  defaultValue?: Multiple extends true ? string[] : string;
  onChange?: (value: Multiple extends true ? string[] : string) => void;
  children?: React.ReactNode;
}

function ChipGroup<Multiple extends boolean = false>(props: ChipGroupProps<Multiple>): React.JSX.Element {
  return <MantineChip.Group<Multiple> {...props} />;
}

export const Chip = Object.assign(ChipBase, { Group: ChipGroup });
